
import { Component, Vue } from "vue-property-decorator";

@Component({})
export default class OAuthCallback extends Vue {
  callbackStorageKey = "__PINPOINT_OAUTH_CALLBACK";
  completeStorageKey = "__PINPOINT_OAUTH_COMPLETE";
  pendingStateStorageKey = "__PINPOINT_OAUTH_PENDING_STATE";
  message = "Completing Microsoft sign-in...";
  alertClass = "alert-info";
  oauthMessageHandler: any = null;
  oauthStorageHandler: any = null;
  oauthBroadcastChannel: any = null;

  mounted() {
    this.listenForLoginCompletion();
    const query = this.$route.query;
    const error = this.firstQueryValue(query.error);
    const errorDescription = this.firstQueryValue(query.error_description);
    const code = this.firstQueryValue(query.code);
    const state = this.firstQueryValue(query.state);
    const sessionState = this.firstQueryValue(query.session_state);
    const pendingState = localStorage.getItem(this.pendingStateStorageKey);

    if (error) {
      this.message = `Microsoft sign-in failed: ${errorDescription || error}`;
      this.alertClass = "alert-danger";
    } else if (!code) {
      this.message = "Microsoft sign-in did not return an authorization code. Please close this window and try again.";
      this.alertClass = "alert-danger";
    } else if (!pendingState || pendingState !== state) {
      this.message = "This Microsoft sign-in response was already handled or is no longer valid. Close this window and start SSO login again.";
      this.alertClass = "alert-warning";
      return;
    }

    const oauthResult = {
      type: "pinpoint-oauth-callback",
      code,
      state,
      sessionState,
      error,
      errorDescription,
      sentAt: new Date().getTime()
    };

    this.publishCallback(oauthResult);

    if (code && !error) {
      this.message = "Microsoft sign-in completed. Finishing PinPoint login...";
    }
  }

  beforeDestroy() {
    if (this.oauthMessageHandler) {
      window.removeEventListener("message", this.oauthMessageHandler);
    }
    if (this.oauthStorageHandler) {
      window.removeEventListener("storage", this.oauthStorageHandler);
    }
    if (this.oauthBroadcastChannel) {
      this.oauthBroadcastChannel.close();
    }
  }

  publishCallback(oauthResult: any) {
    localStorage.removeItem(this.pendingStateStorageKey);

    if (window.opener) {
      window.opener.postMessage(oauthResult, window.location.origin);
    }

    try {
      const broadcastChannel = new BroadcastChannel("pinpoint-oauth");
      broadcastChannel.postMessage(oauthResult);
      broadcastChannel.close();
    } catch (e) {
      // BroadcastChannel may be unavailable in older browsers.
    }

    try {
      localStorage.setItem(this.callbackStorageKey, JSON.stringify(oauthResult));
    } catch (e) {
      // Storage may be unavailable in strict private browsing modes.
    }
  }

  listenForLoginCompletion() {
    const handleCompletion = (result: any) => {
      if (!result || result.type !== "pinpoint-oauth-complete") {
        return;
      }
      if (result.success) {
        this.message = "PinPoint sign-in completed.";
        this.alertClass = "alert-success";
        window.setTimeout(() => window.close(), 250);
      } else {
        this.message = result.message || "PinPoint sign-in failed. Close this window and try again.";
        this.alertClass = "alert-danger";
      }
    };

    this.oauthMessageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      handleCompletion(event.data || {});
    };

    this.oauthStorageHandler = (event: StorageEvent) => {
      if (event.key !== this.completeStorageKey || !event.newValue) {
        return;
      }
      try {
        handleCompletion(JSON.parse(event.newValue));
      } catch (e) {
        // Ignore malformed storage events.
      }
    };

    window.addEventListener("message", this.oauthMessageHandler);
    window.addEventListener("storage", this.oauthStorageHandler);
    try {
      this.oauthBroadcastChannel = new BroadcastChannel("pinpoint-oauth");
      this.oauthBroadcastChannel.onmessage = (event: MessageEvent) => handleCompletion(event.data || {});
    } catch (e) {
      this.oauthBroadcastChannel = null;
    }
  }

  firstQueryValue(value: string | (string | null)[] | null | undefined): string | null {
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }
    return value || null;
  }
}
